import { useMemo, useState } from "react";
import { FolderTree, List, Search } from "lucide-react";
import { CarregandoTabela } from "../../../components/carregando";
import { Input } from "../../../components/ui/input";
import TabelaCategorias from "./TabelaCategorias";
import TabelaProdutos from "./TabelaProdutos";

const CHAVE_VISAO = "estoque_cmv_visao";

function lerVisao() {
  try { return localStorage.getItem(CHAVE_VISAO) === "produtos" ? "produtos" : "categorias"; } catch (_) { return "categorias"; }
}

function Alternador({ visao, onVisao }) {
  const opcoes = [
    { id: "categorias", label: "Por categoria", Icone: FolderTree },
    { id: "produtos", label: "Por produto", Icone: List },
  ];
  return (
    <div className="inline-flex rounded-lg border bg-muted/40 p-0.5">
      {opcoes.map(({ id, label, Icone }) => (
        <button
          key={id}
          type="button"
          onClick={() => onVisao(id)}
          className={`flex items-center gap-1.5 px-3 h-8 rounded-md text-xs font-medium transition-colors ${visao === id ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
        >
          <Icone className="w-3.5 h-3.5" />{label}
        </button>
      ))}
    </div>
  );
}

function Legenda() {
  const faixas = [["bg-emerald-500", "Excelente ≥50%"], ["bg-green-500", "Boa ≥30%"], ["bg-amber-500", "Atenção ≥15%"], ["bg-red-500", "Crítica <15%"]];
  return (
    <div className="flex items-center gap-4 flex-wrap text-[11px] text-muted-foreground">
      <span className="font-semibold uppercase tracking-wider text-[10px]">Margem:</span>
      {faixas.map(([cor, txt]) => <span key={txt} className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${cor}`} />{txt}</span>)}
    </div>
  );
}

/**
 * Seção "CMV" do Relatório de Estoque. `linhas` vem pronto de pages/Reports.js
 * (custo da ficha ao vivo + vendas de 30 dias). Por categoria é o padrão.
 * `carregando`: primeira abertura — a tabela pisca no lugar de "Nenhum produto".
 */
export default function SecaoCmv({ linhas, carregando = false }) {
  const [visao, setVisao] = useState(lerVisao);
  const [busca, setBusca] = useState("");
  const [sort, setSort] = useState({ key: "margin", dir: "asc" });
  const [abertas, setAbertas] = useState(() => new Set());

  const termo = busca.toLowerCase().trim();
  const filtradas = useMemo(() => (termo
    ? linhas.filter((r) => (r.name || "").toLowerCase().includes(termo) || (r.category || "").toLowerCase().includes(termo))
    : linhas), [linhas, termo]);

  const trocarVisao = (v) => { setVisao(v); try { localStorage.setItem(CHAVE_VISAO, v); } catch (_) { /* só conveniência */ } };
  const ordenarPor = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const alternar = (nome) => setAbertas((prev) => { const n = new Set(prev); n.has(nome) ? n.delete(nome) : n.add(nome); return n; });
  const todasCategorias = [...new Set(filtradas.map((l) => l.category || "Sem categoria"))];
  const tudoAberto = todasCategorias.length > 0 && todasCategorias.every((c) => abertas.has(c));

  return (
    <section className="mb-3">
      <div className="mb-3 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold">CMV {visao === "categorias" ? "por Categoria" : "por Produto"}</h2>
          <p className="text-[11px] text-muted-foreground">
            {visao === "categorias" ? "Clique numa categoria para ver os produtos · clique nos cabeçalhos para reordenar" : "Clique nos cabeçalhos para reordenar"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Alternador visao={visao} onVisao={trocarVisao} />
          {visao === "categorias" && !termo ? (
            <button type="button" onClick={() => setAbertas(tudoAberto ? new Set() : new Set(todasCategorias))} className="h-8 px-3 rounded-lg border text-xs font-medium hover:bg-muted transition-colors">
              {tudoAberto ? "Fechar todas" : "Abrir todas"}
            </button>
          ) : null}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar produto ou categoria..." className="pl-9 h-9 w-64" />
          </div>
          {!carregando && <span className="text-xs text-muted-foreground whitespace-nowrap">{filtradas.length} {filtradas.length === 1 ? "produto" : "produtos"}</span>}
        </div>
      </div>

      <div className="border rounded-xl overflow-hidden mb-3">
        {carregando ? (
          <CarregandoTabela linhas={8} colunas={6} className="" />
        ) : filtradas.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm">
            {termo ? `Nenhum produto encontrado para "${busca}"` : "Nenhum produto com preço ou CMV"}
          </div>
        ) : (
          <div className="overflow-auto">
            {visao === "categorias"
              ? <TabelaCategorias linhas={filtradas} sort={sort} onSort={ordenarPor} abertas={abertas} onToggle={alternar} buscando={!!termo} />
              : <TabelaProdutos linhas={filtradas} sort={sort} onSort={ordenarPor} />}
          </div>
        )}
      </div>
      <Legenda />
    </section>
  );
}
